import type { Gender, MemberSummaryPublic, UserCreate, UserDocumentCreate, UserPublic } from '../type/user';
import type { MemberTransactionPublic } from '../type/transaction';
import { toThreeDigits } from '@/lib/utils';
import type { Member, MemberFormData, MemberOverview, MemberTransaction } from '@/types';

const toDisplayCase = (value?: string | null) => {
  if (!value) return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const toNameCase = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  return trimmed.replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const UserAPIResponseMapper = (user: UserPublic): Member => {
  const contact = user.contacts?.[0];
  const documents = user.documents ?? [];

  return {
    id: String(user.id),
    memberNo: user.member_id != null ? toThreeDigits(user.member_id) : undefined,
    name: user.full_name ?? '',
    fatherOrHusbandName: user.father_husband_name ?? undefined,
    gender: toDisplayCase(user.gender) as Member['gender'],
    dob: user.date_of_birth ?? undefined,
    aadhaarNo: user.aadhaar_no ?? undefined,
    educationQualification: user.education_qualification ?? undefined,
    occupation: user.occupation ?? undefined,
    phone: contact?.phone ?? '',
    email: user.email || undefined,
    photo: user.profile_photo?.url,
    profileImage: user.profile_photo?.url,
    joinedDate: user.joined_date ?? '',
    proof1: documents[0]?.document_name,
    proof1Url: documents[0]?.file_url,
    proof2: documents[1]?.document_name,
    proof2Url: documents[1]?.file_url,
    address: contact?.address ?? undefined,
    lifeStatus: (user.life_status?.toLowerCase() as Member['lifeStatus']) ?? 'alive',
    status: user.status ?? undefined,
    policyCount: user.policy_count ?? undefined,
    cardNo: user.card_no || undefined,
  };
};

export const UserAPIRequestMapper = (form: MemberFormData): UserCreate => {
  const documents: UserDocumentCreate[] = [
    { document_name: form.proof1, file_url: form.proof1Url },
    { document_name: form.proof2, file_url: form.proof2Url },
  ]
    .filter((doc) => doc.file_url)
    .map((doc) => ({ document_name: doc.document_name || 'Document', file_url: doc.file_url! }));

  return {
    email:form.email?.trim() || null,
    full_name: toNameCase(form.name),
    father_husband_name: form.fatherOrHusbandName?.trim() ? toNameCase(form.fatherOrHusbandName) : null,
    gender: toDisplayCase(form.gender) as Gender,
    date_of_birth: form.dob,
    age: form.age ?? null,
    aadhaar_no: form.aadhaarNo || null,
    education_qualification: form.educationQualification?.trim() || null,
    occupation: form.occupation?.trim() || null,
    joined_date: form.joinedDate,
    // undefined = keep current photo, '' = remove photo, URL = new photo.
    profile_photo: form.photo,
    contacts: [
      {
        phone: form.phone,
        email:form.email?.trim() || null,
        address: form.address?.trim() || null,
        is_primary: true,
      },
    ],
    documents,
  };
};

export const MemberOverviewMapper = (summary?: MemberSummaryPublic | null): MemberOverview | null =>
  summary
    ? {
        totalMembers: summary.total_members,
        male: summary.male_members,
        female: summary.female_members,
        joinedThisMonth: summary.joined_this_month,
        joinedLastMonth: summary.joined_last_month,
        joinedThisYear: summary.joined_this_year,
        withPolicy: summary.members_with_policy,
        activePolicies: summary.active_policies,
        withoutPolicy: summary.members_without_policy,
        under5: summary.members_under_5,
        age60AndAbove: summary.members_60_and_above,
        deceased: summary.deceased_members,
        alive: summary.alive_members,
        claimsSettled: summary.claims_settled,
      }
    : null;

export const MemberTransactionMapper = (item: MemberTransactionPublic): MemberTransaction => ({
  id: String(item.id),
  date: item.date,
  kind: item.kind,
  type: item.type,
  policyNo: item.policy_no || undefined,
  loanNo: item.loan_no || undefined,
  paymentType: item.payment_type || undefined,
  amount: item.amount,
  note: item.note || undefined,
});
